import SyncIcon from "@mui/icons-material/Sync";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import type {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import type {SimphonyConfig} from "crqlar-react-common/dist/models/integrations/simphony/simphony.config";
import {SyncType} from "crqlar-react-common/dist/models/integrations/syncTask.model";
import {SimphonyTaskService} from "crqlar-react-common/dist/services/integrations/simphony/simphonyTask.service";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import SyncTasksTable from "../../common/SyncTasksTable";

interface Props {
    configData: UseEntityData<SimphonyConfig>
}

/** How many recent tasks to look through when deciding whether this connector ever synced. */
const NEVER_SYNCED_SCAN_LIMIT = 50;

const SimphonyTasks = ({configData}: Props) => {
    const {t} = useTranslation();
    const configId = configData.item.id;
    const quickSyncEnabled = configData.item.pollerConfig?.quickSync?.enabled === true;

    // Undefined while unknown: the warning must not flash up before the answer is in.
    const [neverSynced, setNeverSynced] = useState<boolean>();

    useEffect(() => {
        if (!configId || !quickSyncEnabled) {
            setNeverSynced(false);
            return;
        }

        let cancelled = false;

        SimphonyTaskService.getAll({
            configIds: [configId],
            limit: {offset: 0, limit: NEVER_SYNCED_SCAN_LIMIT}
        })
            .then(page => {
                if (cancelled) return;
                const succeeded = (page.content || []).some(task =>
                    task.status === 'SUCCESS'
                    && (task.syncType === SyncType.FULL_SYNC || task.syncType === SyncType.QUICK_SYNC)
                );
                setNeverSynced(!succeeded);
            })
            .catch(() => {
                // A failed lookup is not evidence of anything; stay quiet rather than warn wrongly.
                if (!cancelled) setNeverSynced(false);
            });

        return () => {
            cancelled = true;
        };
    }, [configId, quickSyncEnabled]);

    return (
        <Box>
            {/*
              * Automatic quick syncs only start once a full or quick sync has completed
              * successfully - the scheduler chains each one off the last success, so it has
              * nothing to chain from until then. The backend now seeds that first sync by itself,
              * but a connector whose first attempt keeps failing would otherwise sit here looking
              * switched on and doing nothing, which is exactly how this was missed on the sandbox.
              */}
            {neverSynced && (
                <Alert severity="warning" sx={{mb: 2}}>
                    <AlertTitle>{t('Waiting for the first sync')}</AlertTitle>
                    {t('Automatic syncing starts once one sync has completed successfully, and this connector has not completed one yet. The first sync is scheduled automatically; if nothing appears below, press "Reconcile open checks" and check the task for errors.')}
                </Alert>
            )}
            <SyncTasksTable
                configId={configId}
                tableId="system-simphony-sync-tasks"
                list={SimphonyTaskService.getAll}
                actions={[
                    {
                        id: 'btn-schedule-daily-sync',
                        label: t('Schedule daily sync'),
                        startIcon: <SyncIcon/>,
                        onClick: () => SimphonyTaskService.scheduleDailySync(configId!)
                    },
                    {
                        // Deliberately not called "full sync": Simphony exposes no endpoint that lists
                        // past checks, so nothing can walk history. This re-reads what is open now plus
                        // every check already being followed.
                        id: 'btn-schedule-full-sync',
                        label: t('Reconcile open checks'),
                        startIcon: <RestartAltIcon/>,
                        onClick: () => SimphonyTaskService.scheduleFullSync(configId!)
                    }
                ]}
            />
        </Box>
    );
};

export default SimphonyTasks;
